import { useDocumentTitle } from "usehooks-ts";
import { DataTableContextProvider } from "../DataTable/DataTableContext";
import { DataTable } from "../DataTable/DataTable";
import { RenewalsSummary } from "./RenewalsSummary";
import { MailListEntry, RenewalTableItem } from "../../utils/quoteUtils";
import { EmptyCell } from "../DataTable/EmptyCell";
import { TableSettingsButton } from "../DataTable/TableSettingsButton";
import { ContactsCell } from "./ContactsCell";
import { QuotesCell } from "./QuotesCell";
import { MetadataCell } from "./MetadataCell";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import Button from "../../../student/components/generic/button";
import clsx from "clsx";
import { useUpdateRenewal } from "./useUpdateRenewal";
import {
  DEFAULT_SCHOOL_YEAR,
  RENEWALS_TABLE_QUERY_KEY,
  SCHOOL_YEAR_CHOICES,
} from "./constants";
import { RenewalDisabledCell } from "./RenewalDisabledCell";
import { downloadMailList } from "./downloadMailList";

export const RenewalsPage: React.FC = () => {
  useDocumentTitle("Renewals");

  const { schoolYear } = useParams();
  const navigate = useNavigate();
  const updateRenewal = useUpdateRenewal();

  useEffect(() => {
    if (!schoolYear) {
      navigate(`./${DEFAULT_SCHOOL_YEAR}`);
    }
  }, [navigate, schoolYear]);

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="flex items-center gap-8">
        <h1 className="text-2xl font-bold">Renewals</h1>
        <ul className="flex gap-6">
          {SCHOOL_YEAR_CHOICES.map(({ key, text }) => (
            <li key={key}>
              <Button
                className={clsx(schoolYear === key ? "!px-4" : "")}
                href={`../renewals/${key}`}
                type={schoolYear === key ? "primary" : "link"}
              >
                {text}
              </Button>
            </li>
          ))}
        </ul>
      </header>
      <RenewalsSummary />
      <DataTableContextProvider
        queryKey={RENEWALS_TABLE_QUERY_KEY}
        source={`/manager_new/renewals/all/${schoolYear}`}
        sortable={[
          "nces",
          "weight",
          "renewalWeightCalculated",
          "renewalCleared",
          "renewalPlusDowngrade",
          "renewalGradeDowngrade",
        ]}
        filterable={["name", "nces", "contacts"]}
        defaults={{ sortBy: [{ id: "nces", desc: false }] }}
        getRowProps={(row) =>
          row.original.disabled
            ? {
                className: "[&>*]:opacity-30",
                title: "This renewal is disabled",
              }
            : {}
        }
        globalFilter
        fetchOnLoad
        columnOverrides={{
          contacts: ({ cell }: any) => (
            <ContactsCell
              key={`contacts-${cell.row.original._id}-${(
                cell.row.original.contacts as MailListEntry[]
              )
                .map(({ email }) => email)
                .join(",")}`}
              renewal={cell.row.original}
            />
          ),
          quotes: ({ cell }: any) => (
            <QuotesCell
              key={`quotes-${cell.row.original._id}`}
              quotes={cell.value}
            />
          ),
          renewalQuoteMetadata: ({ cell }: any) => (
            <MetadataCell
              key={`meta-${cell.row.original._id}`}
              meta={cell.value}
            />
          ),
          disabled: ({ cell }: any) => (
            <RenewalDisabledCell
              key={`disabled-${cell.row.original._id}`}
              value={!!cell.value}
              renewal={cell.row.original}
            />
          ),
          renewalCleared: ({ cell }: any) => {
            const renewal = cell.row.original as RenewalTableItem;
            const renewalPercentComplete = renewal.renewalWeightCalculated ?? 0;
            return (
              <input
                key={`renewalCleared-${cell.row.original._id}`}
                type="checkbox"
                checked={!!cell.value}
                disabled={
                  !!renewal.renewalCleared &&
                  (renewalPercentComplete > 0.75 ||
                    (renewal.type === "individual" &&
                      renewalPercentComplete > 0.5))
                }
                className="disabled:cursor-not-allowed disabled:opacity-50"
                onChange={() =>
                  updateRenewal(cell.row.original._id, {
                    renewalCleared: !cell.value,
                  })
                }
              />
            );
          },
          nonRenewalConfirmed: ({ cell }: any) =>
            !cell.row.values.renewalWeight ? (
              <input
                key={`nonRenewalConfirmed-${cell.row.original._id}`}
                type="checkbox"
                checked={!!cell.value}
                onChange={() =>
                  updateRenewal(cell.row.original._id, {
                    nonRenewalConfirmed: !cell.value,
                  })
                }
              />
            ) : (
              <EmptyCell />
            ),
          renewalNotes: ({ cell }: any) => (
            <textarea
              key={`renewalNotes-${cell.row.original._id}`}
              className="h-24 w-40 rounded-lg border-2 border-gray-300 p-2"
              defaultValue={cell.value}
              onBlur={(e) => {
                const oldNote = cell.value;
                const newNote = e.target.value.trim();
                if (
                  oldNote !== newNote &&
                  !(
                    (!oldNote && newNote === "") ||
                    (oldNote === "" && !newNote)
                  )
                ) {
                  updateRenewal(cell.row.original._id, {
                    renewalNotes: newNote,
                  });
                }
              }}
            />
          ),
        }}
        customButtons={[
          <TableSettingsButton
            key="download-mail-list"
            onClick={async () => {
              await downloadMailList();
            }}
          >
            <i className="fas fa-file-download" />
            Download Mail Lists
          </TableSettingsButton>,
        ]}
      >
        <DataTable />
      </DataTableContextProvider>
    </main>
  );
};
